import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Assistant } from "@qitech/dash-agent-sdk";
import { useTranslation } from "react-i18next";
import { requestChatGrant } from "./agentSession";

const GATEWAY_BASE_URL = process.env.REACT_APP_DASH_AGENT_GATEWAY_URL;

const DEFAULT_PANEL_WIDTH = 380;
const MIN_PANEL_WIDTH = 300;
const MAX_PANEL_WIDTH = 700;

/* The dash agent is one assistant for the whole CaaS dashboard — the rule engine is a skill it
   has, not what it is. So this component knows nothing about any screen: a screen mounts it and
   hands over the context its own domain can describe, through the two props below.

   Context reaches the agent in two shapes, mirroring the SDK's two senders:
     chatContext — rides the first submission of each chat, once. For what describes the
                   environment the user is working in and does not change mid-conversation.
     turnContext — rides the next message only. For what the user is looking at right now,
                   which is re-sent whenever it actually changes and skipped when it has not.

   Both are compared by value, so a caller may rebuild the arrays on every render. */
function AgentChat({ chatContext, turnContext, title, greeting, emptySubtitle, suggestions }) {
    const { t } = useTranslation();

    const [reservedWidth, setReservedWidth] = useState(0);
    const [animateReflow, setAnimateReflow] = useState(true);

    /* The SDK hands these senders back once it has mounted, so they are state rather than refs —
       the effects below must re-run the moment they arrive. */
    const [queueTurnContext, setQueueTurnContext] = useState(null);
    const [queueChatContext, setQueueChatContext] = useState(null);

    const handleContextSender = useCallback((sender) => setQueueTurnContext(() => sender), []);
    const handleChatContextSender = useCallback((sender) => setQueueChatContext(() => sender), []);

    const turnContextJson = useMemo(
        () => (turnContext && turnContext.length > 0 ? JSON.stringify(turnContext) : null),
        [turnContext]
    );
    const chatContextJson = useMemo(
        () => (chatContext && chatContext.length > 0 ? JSON.stringify(chatContext) : null),
        [chatContext]
    );

    useEffect(() => {
        if (!queueTurnContext || !turnContextJson) return;
        queueTurnContext(JSON.parse(turnContextJson));
    }, [queueTurnContext, turnContextJson]);

    useEffect(() => {
        if (!queueChatContext || !chatContextJson) return;
        queueChatContext(JSON.parse(chatContextJson));
    }, [queueChatContext, chatContextJson]);

    const handleWidthChange = useCallback((width, options) => {
        setReservedWidth(width);
        setAnimateReflow(!options || options.animate !== false);
    }, []);

    if (!GATEWAY_BASE_URL) return null;

    return (
        <React.Fragment>
            {/* The panel paints as a fixed rail, so the host reserves its width here and the
                screen behind it reflows instead of being covered. */}
            <div
                style={{
                    width: reservedWidth + "px",
                    flexShrink: 0,
                    transition: animateReflow ? "width .2s ease" : "none"
                }}
            />
            <Assistant
                gatewayBaseUrl={GATEWAY_BASE_URL}
                getChatGrant={requestChatGrant}
                title={title || t("Assistente")}
                greeting={greeting}
                emptySubtitle={emptySubtitle}
                suggestions={suggestions}
                displayMode="push"
                side="right"
                defaultWidth={DEFAULT_PANEL_WIDTH}
                minWidth={MIN_PANEL_WIDTH}
                maxWidth={MAX_PANEL_WIDTH}
                resizable
                onWidthChange={handleWidthChange}
                onContextSender={handleContextSender}
                onChatContextSender={handleChatContextSender}
            />
        </React.Fragment>
    );
}

export default AgentChat;
